import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Button } from "@mui/material";

import proride from "../../assets/proride.webp";
import sovereign from "../../assets/sovereign-1.png";
import hartex from "../../assets/hartex.webp";
import master from "../../assets/master.webp";
import prorideLogo from "../../img/banners/proride-logo-2.png";
import sovereignLogo from "../../img/banners/sovereign-logo.png";
import hartexLogo from "../../assets/hartex-logo.png";
import masterLogo from "../../img/banners/proride-logo-1.png";

gsap.registerPlugin(ScrollTrigger);

const brands = [
  {
    name: "proride",
    image: proride,
    logo: prorideLogo,
    tagline: "Grips, Fenders & Bottles",
  },
  {
    name: "sovereign",
    image: sovereign,
    logo: sovereignLogo,
    tagline: "Premium Saddles",
  },
  {
    name: "hartex",
    image: hartex,
    logo: hartexLogo,
    tagline: "Tires",
  },
  {
    name: "master",
    image: master,
    logo: masterLogo,
    tagline: "Bushless Chains",
  },
];

function Vocal4Local({ onBrandClick }) {
  const headingRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate heading (fade-in + slide-up)
      gsap.from(headingRef.current, {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: headingRef.current,
          start: "top 80%",
          toggleActions: "play none none none", // play only once
        },
      });

      // Animate brand cards with stagger (fade + scale-up)
      gsap.from(cardsRef.current, {
        opacity: 0,
        y: 50,
        scale: 0.9,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.2,
        scrollTrigger: {
          trigger: headingRef.current, // starts when heading comes in
          start: "top 80%",
          toggleActions: "play none none none",
        },
      });
    });

    return () => ctx.revert(); // Cleanup GSAP context on unmount
  }, []);

  return (
    <section className="w-full bg-gradient-to-b from-white-1 to-gray-50">
      {/* Heading */}
      <div ref={headingRef} className="text-center px-4 pt-8 pb-4">
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-wide">
          <span className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 bg-clip-text text-transparent">
            Vocal4Local
          </span>
        </h2>
        <p className="text-gray-500 mt-1 text-lg">
          Discover & Support New Indian Brands
        </p>
      </div>

      {/* Brand Cards */}
     <div className="flex flex-nowrap gap-6 overflow-x-auto lg:overflow-visible lg:grid lg:grid-cols-4 lg:gap-6 px-6 -mt-10">

        {brands.map((brand, index) => (
          <div
            key={brand.name}
            ref={(el) => (cardsRef.current[index] = el)}
            className="w-[220px] lg:w-full h-full bg-white-1 rounded-xl shadow-md hover:shadow-2xl cursor-pointer relative flex-shrink-0 group overflow-hidden transition-transform duration-300 hover:scale-105"
            onClick={() => onBrandClick(brand.name)}
          >
            {/* Brand Image */}
            <img
              src={brand.image}
              alt={brand.name}
              className="w-full h-full object-cover rounded-xl transition-transform duration-500 group-hover:scale-110"
            />

            {/* Overlay */}
            <div className="absolute bottom-0 left-0 w-full h-[180px] p-1.5">
              <div className="w-full h-full bg-white-1 bg-opacity-95 rounded-xl flex flex-col items-center justify-center text-center px-3 py-2">
                <img
                  src={brand.logo}
                  alt={`${brand.name}-logo`}
                  className="h-8 mb-2 transition-transform duration-300 group-hover:scale-105"
                />
                <p className="text-gray-800 font-semibold text-sm">
                  {brand.tagline}
                </p>
                <Button
                  variant="contained"
                  sx={{
                    marginTop: "10px",
                    borderRadius: "25px",
                    background: "linear-gradient(90deg, #ff4d6d, #4f46e5)",
                    fontSize: "12px",
                    fontWeight: "500",
                    textTransform: "none",
                    padding: "4px 14px",
                    boxShadow: "0 4px 12px rgba(79,70,229,0.3)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #4f46e5, #ff4d6d)",
                      boxShadow: "0 6px 16px rgba(255,77,109,0.4)",
                      scale: 1.05,
                      transition: "all 0.3s ease-in-out",
                    },
                  }}
                  onClick={() => onBrandClick(brand.name)}
                >
                  Shop Now
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Vocal4Local;
